import React from 'react'
import { observer } from 'mobx-react'
import { action, observable } from 'mobx'
import isArray from 'lodash/isArray'
import {
  cartContainer,
  checkoutContainer,
  customerContainer,
  addressContainer,
  tokenExContainer,
  paymetricContainer,
  paypalContainer,
} from 'src/models'
import {
  GlobalPaymentsOptionsList,
  toastState,
  AddressValidation,
} from 'src/views/components'
import {
  handleAddressValidation,
  creditCardValidation,
  isToShowLegalAddressOptionInAddressForm,
} from 'src/deps'
import ExpressSectionHeader from '../../ExpressSectionHeader'
import {
  getNamefromFirstname,
  trackCustomCheckoutEvent,
  trackAddPaymentInfoEvent,
  isGPSEnabled,
  i18nTranslate,
  isExpressCheckout,
  isLATAM,
  getLocalStorage,
  setLocalStorage,
  deleteFromLocalStorage,
  enableNewFeature,
  convertToBoolean,
  getGPSConfig,
  isFeatureEnabled,
  enableCartMFE,
  getLiveEventStatus,
  isExternalGPSCreditCardPaymentName,
  isExternalGPSCreditCardOption,
  getConfiguredExternalGPSCreditCardOption,
  isPaymetric3DSProcessor,
  isCSRActOnBehalfSession,
} from 'src/utils'
import { APPConfig } from 'config/appConfig'
import { isHomeCountry } from 'src/utils/legalTaxUtils'
import { overlayState } from '../../Overlay'
import isEmpty from 'lodash/isEmpty'

@observer
class GlobalPaymentAccordion extends React.Component {
  @observable paymentList = []
  @observable initiallySelectedPayment = []
  @observable isPaymentsApiLoaded = false
  @observable cvvValidFlag = false
  @observable isGpsPaymentsApiLoaded = false
  @observable gpsPaymentList = ''
  isLiveEvent = convertToBoolean(getLiveEventStatus())

  constructor(props) {
    super(props)

    this.state = {
      isToShowAddressValidationModal: false,
      addressInvalidFlag: false,
      recommendations: [],
      params: {},
    }
  }

  /**
   * @description Returns the appropriate payment container based on Paymetric flag
   * @returns {Object} tokenExContainer or paymetricContainer
   */
  getPaymentContainer = () => {
    const enableDirectPaymetricAndAccertifyIntegration = convertToBoolean(
      APPConfig.getAppConfig().enableDirectPaymetricAndAccertifyIntegration
    )
    return enableDirectPaymetricAndAccertifyIntegration
      ? paymetricContainer
      : tokenExContainer
  }

  @action
  /**
   * @description Sets the cvvValidFlag to the provided value.
   * @param {boolean} val - The new value to set cvvValidFlag to.
   */
  changeCvvValidFlag = val => {
    this.cvvValidFlag = val
  }
  isInternalPayment = () => {
    const paymentDetails =
      checkoutContainer.globalPaymentResponse['CREDITCARD'] || []
    const processorURL = paymentDetails?.[0]?.processorURL || ''
    const isInternalPayment = processorURL
      ?.toLocaleLowerCase()
      ?.includes('internal')
    return isInternalPayment
  }

  isPlainCvvGPSCreditCardPayment = paymentData => {
    const gpsCreditCardOptions =
      checkoutContainer.globalPaymentResponse?.CREDITCARD || []
    const configuredGPSCreditCardProcessorName =
      APPConfig.getAppConfig()?.gpsSavedCardProcessorName
    const resolvedProcessorName =
      getConfiguredExternalGPSCreditCardOption(
        gpsCreditCardOptions,
        configuredGPSCreditCardProcessorName
      )?.name ??
      (!configuredGPSCreditCardProcessorName
        ? gpsCreditCardOptions.find(isExternalGPSCreditCardOption)?.name
        : undefined)
    return isExternalGPSCreditCardPaymentName(
      paymentData?.name ?? resolvedProcessorName,
      gpsCreditCardOptions
    )
  }

  // Configured external GPS credit-card processors, such as PayU or Braintree,
  // are added through the credit-card form but stored on cart as GPS payments.
  // In express checkout, adding another such card must replace the previous card
  // payment before addPaymentToCart runs. Loyalty and store credit stay intact.
  deleteExistingExpressCardPayment = async () => {
    if (!isExpressCheckout()) {
      return
    }

    const customPaymentTypes = new Set(['LOYALTY', 'STORECREDIT'])
    const paymentsToDelete = (
      cartContainer?.cartResponse?.payments || []
    ).filter(payment => payment?.id && !customPaymentTypes.has(payment?.type))

    if (!paymentsToDelete.length) {
      return
    }

    await Promise.all(
      paymentsToDelete.map(payment =>
        checkoutContainer.deletePaymentById(payment.id, true)
      )
    )
    await cartContainer.viewCart()
  }

  /**
   * @description
   * Triggered Delete Payments API to delete already added Payments in Cart.
   * If GPS is Enabled, then Global PaymentOptions API is triggered and on Success Response,
   * If Credit Card Payment Type is supported, GetPayments API is triggered.
   * If GPS is Disabled, GetPayments API is triggered to fetch Saved Card Details.
   */
  getPaymentDetails = async () => {
    let appliedPaymentsInCart = cartContainer?.cartResponse?.payments || []
    const totalAmountUnpaid =
      cartContainer?.cartResponse?.paymentValue?.totalAmountUnpaid || 0

    /**
     * @description
     * when grandtotal is paid with StoreCredit or loyalty completely,
     * user is not allowed to open CreditCard section and submit order button will be enabled
     * if user tries to force open CreditCard section,
     * then loyalty and StoreCredit payment will be auto deleted
     */
    await appliedPaymentsInCart?.map(async payment => {
      const { type = '', id = '', name = '' } = payment || {}
      let isToDeletePayment = true
      let paymentTypesToDelete = ['LOYALTY', 'STORECREDIT']
      const isGPSredirected = getLocalStorage('gpsRedirection') || false

      if (totalAmountUnpaid !== 0 && paymentTypesToDelete?.includes(type)) {
        isToDeletePayment = false
      }

      const PaymentProviderEnum = Object.freeze({
        RAZORPAY: 'Razorpay',
      })
      const isGPSCreditCardPayment = isExternalGPSCreditCardPaymentName(
        name,
        checkoutContainer.globalPaymentResponse?.CREDITCARD || []
      )
      if (
        (name === PaymentProviderEnum.RAZORPAY || isGPSCreditCardPayment) &&
        isGPSredirected
      ) {
        isToDeletePayment = false
      }
      const shouldSkipDeletion =
        name?.toLowerCase() === 'chile_webpay' ||
        (name?.toLowerCase() === 'worldpay payment page (wppg)' &&
          getGPSConfig('enableKlarnaGPSFlow'))
      !shouldSkipDeletion &&
        isToDeletePayment &&
        (await checkoutContainer.deletePaymentById(id))
    })

    const enableGlobalPaymentSystem = isGPSEnabled()
    const isGPSRedirectionMount = !!this.props.gpsPaymentStatus?.()
    if (
      enableGlobalPaymentSystem &&
      enableCartMFE() &&
      !isGPSRedirectionMount
    ) {
      await cartContainer.viewCart()
    }
    const isSubscriptionProductInCart = cartContainer.hasSubscriptionProduct()
    if (enableGlobalPaymentSystem) {
      const response = await checkoutContainer.getGlobalPaymentOptions(
        isSubscriptionProductInCart
      )
      if (checkoutContainer.isSuccessResponse(response)) {
        this.gpsPaymentList = response.response
        this.isGpsPaymentsApiLoaded = true
        const isCreditCardTypeSupportedInGPS =
          (Object.keys(response?.response)?.length > 0 &&
            Object.keys(response?.response)?.some(
              paymentType => paymentType?.toLowerCase() === 'creditcard'
            )) ||
          false
        isCreditCardTypeSupportedInGPS && (await this.getSavedPaymentsList())
      }
    } else {
      await this.getSavedPaymentsList()
    }
    this.isPaymentsApiLoaded = true
  }

  /**
   * @description Fetches saved payment details for the logged in user from the getPayment API.
   * Transforms the response to support Pagination and sets the default selected card if available.
   */
  getSavedPaymentsList = async () => {
    if (customerContainer.isRegisterUser) {
      const response = await customerContainer.getPayment()
      const transformedPaymentResponse =
        customerContainer.transformPaymentResponse(response) || {}
      if (checkoutContainer.isSuccessResponse(response)) {
        this.getDefaultCard(transformedPaymentResponse?.payments || [])
      }
    }
  }

  /**
   * @description Added Conditions to disable Submit Order and Payment Continue buttons.
   * Triggers getPaymentDetails() to fetch payment info.
   */
  async componentDidMount() {
    checkoutContainer.isToInitiateGlobalPayment = false
    checkoutContainer.isToDisableSubmitOrderButton = true
    await this.getPaymentDetails()
  }

  /**
   * @description Function to find the Default Saved Card from the Payments List.
   * @param {*} paymentResponse
   */
  getDefaultCard = (paymentResponse = []) => {
    if (Array.isArray(paymentResponse) && paymentResponse?.length > 0) {
      this.initiallySelectedPayment = paymentResponse?.find(card => {
        return card.isDefault === true
      })
    }

    this.paymentList = paymentResponse
  }

  /**
   * @description Splits payment data into accountInfo and billingAddress objects.
   * @param {Object} props - Object containing data and paymentData
   * @param {Object} data - Address data
   * @param {Object} paymentData - Payment data from form
   * @returns {Object} Object containing accountInfo and billingAddress
   */
  splitData = props => {
    const { data, paymentData } = props
    const {
      cardFirstName = '',
      cardCVV = '',
      cardNumber = '',
      provider = '',
      expiryDate = '',
      setToDefaultPayment = false,
      addPaymentToProfile = false,
      ...remainingProps
    } = paymentData || {}

    const card =
      cardNumber &&
      Object.entries(creditCardValidation)?.find(cardArr => {
        return cardNumber?.match(cardArr?.[1])
      })

    delete paymentData.expiryDate
    const name = getNamefromFirstname(cardFirstName)
    const accountInfo = {
      name: name?.firstName || '',
      lastName: name?.lastName || '',
      cardNumber,
      type: provider,
      expiryMonth: expiryDate?.split('/')[0],
      expiryYear: '20' + expiryDate?.split('/')[1],
      cardCVV,
      ...(isArray(card) && card[0] && { provider: card[0] }),
    }
    const addressData = paymentData.billingSameAsShipping
      ? data
      : paymentData.isFromSavedAddress
        ? paymentData.billingAddress
        : remainingProps
    const email =
      addressData?.email || customerContainer?.profileResponse?.email || ''
    const billingAddress = {
      ...addressData,
      addressLine2: addressData?.addressLine2 || '',
      email,
      canReceiveSMS: false,
      option: {
        addAddressToProfile: false,
        addPaymentToProfile: addPaymentToProfile,
        overrideAddressValidation: false,
        useDefaultAddress: 'false',
        setAsDefaultAddressInProfile: false,
      },
      validation: [
        {
          type: 'PHYSICAL',
          validated: true,
          overridden: true,
        },
      ],
    }
    return {
      isDefault: setToDefaultPayment,
      accountInfo,
      billingAddress,
    }
  }

  /**
   * @description Handles Payment Add to Cart Logic
   * Before Adding Payments to Cart, Communication Preference is Patched to cart and,
   * Billing Address Validation API is triggered and Address Suggestion Modal is Shown.
   * Upon selecting an Address, AddPayment APi is triggered.
   */
  handlePayment = async paymentData => {
    if (
      !this.cvvValidFlag &&
      !this.getPaymentContainer()?.tokenExValidData?.isCvvValid &&
      paymentData?.tokenExData?.token
    ) {
      checkoutContainer.isPaymentApiInProgress = false
      checkoutContainer.isToDisableSubmitOrderButton = false
      if (isExpressCheckout()) {
        checkoutContainer.activeSubmitOrderButton = true
      }
      return
    }
    checkoutContainer.isPaymentApiInProgress = true
    paypalContainer.paypalInitiate = false
    const { onContinue, isMultipleAdressActive } = this.props
    const data = cartContainer.cartResponse?.deliveryDetails?.address || ''
    const splitData = this.splitData({
      data,
      paymentData,
    })

    const {
      isFromSavedAddress = false,
      isPONumber = '',
      setToDefaultPayment = false,
      isRewardPoints,
    } = paymentData

    const communicationPreferenceData = isMultipleAdressActive
      ? splitData.billingAddress
      : data

    const communicationResponse =
      await checkoutContainer.setCommunicationPreference(
        communicationPreferenceData
      )

    if (!checkoutContainer.isSuccessResponse(communicationResponse)) {
      console.log(
        '[PAYMETRIC_DEBUG] [Checkout] Communication preference API failed'
      )
      checkoutContainer.isPaymentApiInProgress = false
      checkoutContainer.isToDisableSubmitOrderButton = false
      if (isExpressCheckout()) {
        checkoutContainer.activeSubmitOrderButton = true
      }
      toastState.setToastMessage(
        communicationResponse?.message || communicationResponse?.errorMessage
      )
      return
    }

    const finalData = isPONumber
      ? {
          cardType: 'PURCHASEORDER',
          isDefault: setToDefaultPayment,
          ...paymentData,
          billingAddress: data,
        }
      : isFromSavedAddress || isRewardPoints
        ? paymentData
        : splitData
    delete paymentData.setToDefaultPayment
    // For live events with pickup addresses, skip billing validation to enable 3DS flow
    if (
      !paymentData.billingSameAsShipping &&
      !isFromSavedAddress &&
      !this.isLiveEvent
    ) {
      delete paymentData.cardFirstName
      delete paymentData.cardLastName
      delete paymentData.cardNumber
      delete paymentData.expiryMonth
      delete paymentData.expiryYear
      delete paymentData.expiryDate
      delete paymentData.cardCVV
      delete paymentData.billingSameAsShipping
      delete paymentData.addPaymentToProfile
      delete paymentData.paymentMethodType
      const paymentValue = {
        ...paymentData,
      }
      if (
        checkoutContainer.selectedMarket === 'US' ||
        checkoutContainer.selectedMarket === 'CA' ||
        paymentData.isToMakeValidationApiCall
      ) {
        const res = await addressContainer.validateAddress(paymentValue)

        if (res?.code === 'ADCOVAL0003') {
          console.log(
            '[PAYMETRIC_DEBUG] [Checkout] Tokenize triggered - ADCOVAL0003 address error'
          )
          this.setState({
            isToShowAddressValidationModal: true,
            addressInvalidFlag: true,
            recommendations: [{ ...paymentValue }, []],
            formDataEnter: { ...paymentValue },
            params: { ...finalData },
          })
          if (isExpressCheckout()) {
            checkoutContainer.expressPaymentAdded = false
            overlayState.hideLoader()
          }
        } else if (res?.code == 'ONESOURCE_ADPLGIN_VAL_ERROR') {
          console.log(
            '[PAYMETRIC_DEBUG] [Checkout] Address plugin error - ONESOURCE_ADPLGIN_VAL_ERROR'
          )
          this.setState({
            isToShowAddressValidationModal: false,
          })
          checkoutContainer.isPaymentApiInProgress = false
          checkoutContainer.isToDisableSubmitOrderButton = false
          if (isExpressCheckout()) {
            checkoutContainer.activeSubmitOrderButton = true
          }
          toastState.setToastMessage(
            i18nTranslate('address.NotAdded', 'Address not added')
          )
          return
        } else {
          const recommendationAddress =
            res?.address?.validationDetails?.[0]?.recommendations || []
          let firstAddress = recommendationAddress?.[0] || {}
          if (
            Object.keys(firstAddress)?.length > 0 &&
            handleAddressValidation(paymentValue, firstAddress)
          ) {
            console.log(
              '[PAYMETRIC_DEBUG] [Checkout] Address suggestions provided'
            )
            isLATAM() && overlayState.hideLoader()
            this.setState({
              isToShowAddressValidationModal: true,
              addressInvalidFlag: false,
              recommendations: [{ ...paymentValue }, ...recommendationAddress],
              params: { ...finalData },
              formDataEnter: { ...paymentValue },
            })
          } else {
            this.setState(
              {
                params: { ...finalData },
              },
              () => {
                this.handleClickOnPro(paymentValue)
              }
            )
          }
        }
      } else {
        this.setState(
          {
            addressInvalidFlag: true,
            recommendations: [{ ...paymentValue }, []],
            formDataEnter: { ...paymentValue },
            params: { ...finalData },
          },
          () => {
            this.handleClickOnPro(paymentData)
          }
        )
      }
    } else {
      // Saved-card submits must replace the card payment already on the cart so
      // the order uses the newly selected card's paymentRefId.
      if (
        this.isPlainCvvGPSCreditCardPayment(finalData) ||
        (isExpressCheckout() && isFromSavedAddress)
      ) {
        await this.deleteExistingExpressCardPayment()
      }
      const response = await checkoutContainer.addPaymentsToCart(finalData)
      if (checkoutContainer.isSuccessResponse(response)) {
        checkoutContainer.expressPaymentAdded = true
        checkoutContainer.isToInitiateGlobalPayment = !this.isInternalPayment()
        await cartContainer.viewCart()

        // 3DS GPS Credit Card Redirection Logic
        const enableGlobalPaymentSystem = isGPSEnabled()
        const validProcessorObj =
          checkoutContainer.getValidGPSCreditCardProcessor()
        const isCSRActOnBehalf = isCSRActOnBehalfSession()
        if (
          enableGlobalPaymentSystem &&
          isPaymetric3DSProcessor(validProcessorObj) &&
          !isCSRActOnBehalf
        ) {
          try {
            overlayState.showLoader()
            const isSubscriptionProductInCart =
              cartContainer.hasSubscriptionProduct()
            const domain =
              window?.location?.href || window.location.origin + '/checkout'
            const gpsInitiateResp =
              await checkoutContainer.initiateGlobalPayment(
                domain,
                isSubscriptionProductInCart,
                validProcessorObj
              )
            overlayState.hideLoader()
            if (gpsInitiateResp && gpsInitiateResp.redirectUrl) {
              /** Bundle under the existing giftPayLoad key so the external 3DS redirect doesn't need a new storage key */
              setLocalStorage('giftPayLoad', {
                giftPayLoad: checkoutContainer?.giftPayLoad,
                giftMessage: checkoutContainer?.giftPayLoad
                  ? checkoutContainer?.giftMessage || ''
                  : '',
                dropShippingPayLoad: checkoutContainer?.dropShippingPayLoad,
              })
              window.location.replace(gpsInitiateResp.redirectUrl)
              return
            } else {
              toastState.setToastMessage(
                i18nTranslate(
                  'payment.gpsErrorMessage',
                  'We ran into a technical error, please try again after sometime'
                )
              )
              return
            }
          } catch (err) {
            overlayState.hideLoader()
            toastState.setToastMessage(
              i18nTranslate(
                'payment.gpsErrorMessage',
                'We ran into a technical error, please try again after sometime'
              )
            )
            return
          }
        }

        if (checkoutContainer.enableAutoSubmit && isExpressCheckout()) {
          this.doExpressCheckout()
        }
        const isPaymentInfoEventEnabled = enableNewFeature(
          'enablePaymentInfoGTMEvent'
        )
        if (isPaymentInfoEventEnabled) {
          trackAddPaymentInfoEvent({
            cartData: cartContainer?.cartResponse,
          })
        } else {
          trackCustomCheckoutEvent({
            eventType: 'payment',
            cartResponse: cartContainer?.cartResponse,
          })
        }
        onContinue()
      } else {
        // Refresh the Paymetric iframe session on failure so the user can
        // resubmit with a fresh accessToken (single-use by Worldpay design).
        console.log('[PAYMETRIC_DEBUG] [Checkout] Payment API failed')
        if (isFeatureEnabled('enableDirectPaymetricAndAccertifyIntegration')) {
          paymetricContainer.retryPaymetricSession()
        }
        if (response.code === 'PYMT_DUP_ENTRY_409') {
          toastState.setToastMessage(
            i18nTranslate(
              'payment.duplicatePaymentCard',
              'Payment card already exists'
            ),
            false
          )
        } else if (response.code === 'EOCAP000013') {
          toastState.setToastMessage(
            i18nTranslate(
              'payment.cardExpired',
              'The Credit card has been expired. Please update/change the card to proceed'
            ),
            false
          )
        } else {
          const errorMessage =
            response?.message ||
            i18nTranslate(
              'payment.addPaymentFailure',
              'Sorry, we are unable to add your payment information. Please try again.'
            )
          toastState.setToastMessage(errorMessage)
        }
        if (isExpressCheckout()) {
          checkoutContainer.expressPaymentAdded = false
          overlayState.hideLoader()
        }
      }
    }
    checkoutContainer.isPaymentApiInProgress = false
    checkoutContainer.isToDisableSubmitOrderButton = false
    if (isExpressCheckout()) {
      checkoutContainer.activeSubmitOrderButton = true
    }
  }

  doExpressCheckout = async () => {
    checkoutContainer.isPaymentApiInProgress = false
    checkoutContainer.isToDisableSubmitOrderButton = false
    checkoutContainer.enableAutoSubmit = false
    checkoutContainer.autoPaymentSuccess = true
    checkoutContainer.expressPaymentAdded = true
    checkoutContainer.triggerExpressSubmit = true
  }
  /**
   * @description Address Validation API is triggered to validate User Selected Billing Address
   * Post that, Add Payments API is triggered to patch Payments to Cart.
   * @param {Object} formData
   */
  async handleClickOnPro(formData) {
    console.log(
      '[PAYMETRIC_DEBUG] [Checkout] Resubmitting after address correction/selection'
    )
    const { accountInfo, isDefault } = this.state.params
    const { onContinue } = this.props

    const reqParams = {
      name: formData?.firstName,
      type: 'CREDITCARD',
      accountInfo,
      billingAddress: {
        ...formData,
        email: this.state.params.billingAddress?.email,
        option: {
          addPaymentToProfile:
            this.state.params.billingAddress?.option?.addPaymentToProfile,
        },
      },
      isDefault,
    }
    let response = {}
    if (formData?.selectedIndex <= 0) {
      response = await checkoutContainer?.validateOriginalAddress(reqParams)
    } else {
      response = await checkoutContainer?.addPaymentsToCart(reqParams)
    }
    if (checkoutContainer.isSuccessResponse(response)) {
      checkoutContainer.isToInitiateGlobalPayment = !this.isInternalPayment()
      await cartContainer.viewCart()
      const isPaymentInfoEventEnabled = enableNewFeature(
        'enablePaymentInfoGTMEvent'
      )
      if (isPaymentInfoEventEnabled) {
        trackAddPaymentInfoEvent({
          cartData: cartContainer?.cartResponse,
        })
      } else {
        trackCustomCheckoutEvent({
          eventType: 'payment',
          cartResponse: cartContainer?.cartResponse,
        })
      }
      if (
        isExpressCheckout() &&
        !checkoutContainer.paymentBillingSameAsShippingFlag
      ) {
        checkoutContainer.expressPaymentAdded = true
        document.getElementById('submit-order-button').click()
      }
      onContinue()
    } else {
      // Refresh the Paymetric iframe session on failure so the user can
      // resubmit with a fresh accessToken (single-use by Worldpay design).
      if (isFeatureEnabled('enableDirectPaymetricAndAccertifyIntegration')) {
        paymetricContainer.retryPaymetricSession()
      }
      if (response?.code === 'PYMT_DUP_ENTRY_409') {
        toastState.setToastMessage(
          i18nTranslate(
            'payment.duplicatePaymentCard',
            'Payment card already exists'
          ),
          false
        )
      } else {
        const errorMessage =
          response?.message ||
          i18nTranslate(
            'payment.addPaymentFailure',
            'Sorry, we are unable to add your payment information. Please try again.'
          )
        toastState.setToastMessage(errorMessage)
      }
    }
    this.handleAddressValidationModalClose()
  }

  /**
   * @description Closes the address validation modal.
   */
  handleAddressValidationModalClose = () => {
    this.setState({
      isToShowAddressValidationModal: false,
    })
    if (isExpressCheckout()) {
      checkoutContainer.expressPaymentAdded = false
    }
  }

  render() {
    if (isExpressCheckout() && !this.isLiveEvent) {
      const legalTaxIdGetData =
        checkoutContainer?.accountsTaxIdResponse?.userTax?.length || 0
      const isHomeCountryUser = customerContainer.isRegisterUser
        ? isHomeCountry()
        : true
      const accountType =
        customerContainer?.profileResponse?.accounts?.accountType ||
        'Guest User'
      const isValidJson = !isEmpty(this.props.taxFormInputJson?.[accountType])
      let message = ''
      if (!cartContainer.cartResponse?.deliveryDetails?.address) {
        message = i18nTranslate(
          'expressCheckout.paymentAvailableShippingAddress',
          'Payments will be available once you enter shipping address'
        )
      } else if (
        isToShowLegalAddressOptionInAddressForm() &&
        !this.props.isLegalAddressAvaiable
      ) {
        message = i18nTranslate(
          'checkout.paymentLegalAddressInfo',
          'Payments will be available once you enter Legal Address'
        )
      } else if (
        !checkoutContainer.isTaxFormDataSubmited &&
        isLATAM() &&
        isHomeCountryUser &&
        !legalTaxIdGetData > 0 &&
        isValidJson
      ) {
        message = i18nTranslate(
          'expressCheckout.paymentAvailableLegaltaxId',
          'Payments will be available once you enter Legal tax Id'
        )
      }

      if (message) {
        return (
          <div className="express-payment-list position-relative">
            <ExpressSectionHeader
              title={i18nTranslate(
                'expressCheckout.addPaymentMethods',
                'Payment'
              )}
              disableAccordion={true}
            />
            <div className="emptyShipping-method p-3 mb-2">{message}</div>
          </div>
        )
      }
    }

    if (this.isPaymentsApiLoaded) {
      return (
        <div>
          <GlobalPaymentsOptionsList
            paymentList={this.paymentList}
            initiallySelectedPayment={this.initiallySelectedPayment}
            handlePayment={this.handlePayment}
            isFromCheckout={this.props.isFromCheckout || false}
            onContinue={this.props.onContinue}
            title={this.props?.title}
            changeCvvValidFlag={this.changeCvvValidFlag}
            handlePaypalInitiate={this.props?.handlePaypalInitiate}
            handleAlipayInitiate={this.props?.handleAlipayInitiate}
            closeAlipayInitiate={this.props?.closeAlipayInitiate}
            handleKlarnaInitiate={this.props?.handleKlarnaInitiate}
            handleApplePayInitiate={this.props?.handleApplePayInitiate}
            handleGooglePayInitiate={this.props?.handleGooglePayInitiate}
            isFromPDP={this.props.isFromPDP || false}
            isGpsPaymentsApiLoaded={this.isGpsPaymentsApiLoaded}
            isTaxFormDataSubmited={checkoutContainer.isTaxFormDataSubmited}
            accountsTaxIdResponse={checkoutContainer.accountsTaxIdResponse}
            gpsPaymentList={this.gpsPaymentList}
          />
          {this.state.isToShowAddressValidationModal && (
            <AddressValidation
              open={this.state.isToShowAddressValidationModal}
              addressInvalidFlag={this.state.addressInvalidFlag}
              recommendations={this.state.recommendations}
              handleClose={() => this.handleAddressValidationModalClose()}
              handleClickOnPro={dataAfterValidation =>
                this.handleClickOnPro(dataAfterValidation)
              }
            />
          )}
        </div>
      )
    }

    return <></>
  }
}

export { GlobalPaymentAccordion }
export default GlobalPaymentAccordion
